import React, { useRef, useState } from 'react';
import { IonPage, IonContent, IonHeader, IonToolbar, IonTitle, IonButtons, IonBackButton, useIonRouter, useIonViewWillEnter, IonIcon, IonAlert } from '@ionic/react';
import BackButton from '../../../shared/assets/icons/back-button.svg';
import { getTableOrders, getTables } from '../../../entities/tables';
import { useParams } from 'react-router';
import { Order, OrderItemStatus } from '../../../entities/orders-pickup-delivery/models/types';
import { ChatSkeleton } from '../../../components/ChatSkeleton';
import { person } from 'ionicons/icons';
import { updateTableStatus } from '../../../entities/update-table-status';

const OrderedTable: React.FC = () => {
    const router = useIonRouter();
    const alert = useRef<HTMLIonAlertElement>(null);

    const { id } = useParams<{ id: string }>();
    const [tableData, setTableData] = useState<{
        tableId: string;
        tableNumber: string;
        orders: Order[];
        tableStatus: string;
    } | null>(null);
    const [isLoading, setLoading] = useState(true);

    const handleFreeTable = async (tableId: string, orderId: string | undefined) => {
        try {
            await updateTableStatus(tableId, { status: 'free', orderId: orderId ?? null });
            router.push('/', 'root', 'replace');
        } catch (error) {
            console.error(error);
        }
    };

    const updateStatusTable = async (tableId: string, status: string, orderId: string | undefined) => {
        if (status !== 'occupied') {
            await handleFreeTable(tableId, orderId);
        } else {
            alert.current?.present();
        }
    };

    const loadTableOrders = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const [res, tablesRaw] = await Promise.all([
                getTableOrders(id),
                getTables().catch(() => null),
            ]);
            const ordersList = Array.isArray(res?.orders) ? res.orders : [];
            type TableRow = { id: string; tableNumber: string };
            const tables: TableRow[] = Array.isArray(tablesRaw) ? tablesRaw as TableRow[] : (tablesRaw ? [tablesRaw as TableRow] : []);
            const tableFromList = tables.find((t) => t.id === id);
            const tableNumber = (res?.tableNumber && res.tableNumber !== '—')
                ? res.tableNumber
                : (tableFromList?.tableNumber ?? '—');
            setTableData({
                tableId: res?.tableId ?? id ?? '',
                tableNumber,
                orders: ordersList,
                tableStatus: res?.tableStatus ?? '',
            });
        } catch (error) {
            console.error(error);
            setTableData(null);
        } finally {
            setLoading(false);
        }
    };

    useIonViewWillEnter(() => {
        loadTableOrders();
    }, [id]);

    const rawOrders = tableData?.orders;
    const orders = Array.isArray(rawOrders) ? rawOrders : [];
    const tableNumber = tableData?.tableNumber ?? orders[0]?.tableNumber ?? '—';
    const firstOrderId = orders[0]?.id;

    return (
        <IonPage className="!bg-[#E5E7EB] ion-page-ordered-table">
            <IonHeader className="ion-no-border">
                <IonToolbar className="px-2 sm:px-3">
                    <IonTitle className="!text-base sm:!text-lg font-semibold">
                        Стол №{tableNumber}
                    </IonTitle>
                    <IonButtons slot="start">
                        <IonBackButton text="" icon={BackButton}></IonBackButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="!bg-[#E5E7EB] ion-content-full-height">
                {isLoading ? (
                    <ChatSkeleton count={10} />
                ) : !tableData ? (
                    <div className="mx-auto w-full max-w-md py-8 px-4 text-center text-slate-600 text-sm">
                        Не удалось загрузить данные стола
                    </div>
                ) : orders.length === 0 ? (
                    <div className="mx-auto w-full max-w-md py-8 px-4 text-center text-slate-600 text-sm">
                        Нет оплаченных заказов по этому столу
                    </div>
                ) : (
                    <div className="mx-auto w-full max-w-md px-3 py-3 pb-32 flex flex-col">
                        <div className="space-y-3 flex-1 overflow-y-auto">
                            {orders.map((order, orderIndex) => (
                                <div
                                    key={order?.id ?? orderIndex}
                                    className="rounded-xl bg-white shadow-sm overflow-hidden border border-slate-100"
                                >
                                    <div className="px-3 py-2.5 flex items-center justify-between border-b border-slate-100 bg-slate-50/50">
                                        <span className="text-[13px] font-semibold text-slate-700 truncate min-w-0">
                                            Заказ {orders.length > 1 ? `#${orderIndex + 1}` : ''} {order?.orderNumber ? `№${order.orderNumber}` : ''}
                                        </span>
                                        <span className="text-[13px] font-bold text-slate-900 shrink-0 ml-2">
                                            {Number(order?.totalAmount ?? 0).toFixed(2)} TJS
                                        </span>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {(Array.isArray(order?.items) ? order.items : []).map((item, itemIndex) => (
                                            <div
                                                key={item?.itemId ?? `${order.id}-${itemIndex}`}
                                                className="flex items-stretch gap-2 px-3 py-2.5 min-h-[44px] active:bg-slate-50/80"
                                            >
                                                <div className="flex items-center shrink-0 w-6">
                                                    <IonIcon icon={person} className="text-[#9CA3AF] text-lg" />
                                                </div>
                                                <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                    <p className="text-[13px] font-medium text-slate-900 truncate">{item?.name ?? '—'}</p>
                                                    <p className="text-[11px] text-slate-600 mt-0.5">
                                                        {Number(item?.price ?? 0).toFixed(2)} TJS × {item?.quantity ?? 0}
                                                        {(item?.addedBy?.name) && (
                                                            <span className="ml-1"> · {item.addedBy.name}</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="shrink-0 flex flex-col items-end justify-center text-right">
                                                    <StatusTag status={item?.status ?? 'pending_confirmation'} />
                                                    {(item?.status === 'preparing' || item?.status === 'pending_confirmation' || item?.status === 'served') && item?.addedAt && (
                                                        <span className="text-[10px] text-slate-500 mt-0.5">
                                                            {new Date(item.addedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-3 rounded-xl bg-white shadow-sm border border-slate-100 px-3 py-3">
                            <div className="flex justify-between items-center text-[13px]">
                                <span className="text-slate-600 font-medium">Заказов:</span>
                                <span className="font-bold text-slate-900">{orders.length}</span>
                            </div>
                            <div className="flex justify-between items-center text-[13px] mt-1">
                                <span className="text-slate-600 font-medium">Сумма:</span>
                                <span className="font-bold text-slate-900">
                                    {orders.reduce((sum, o) => sum + Number(o?.totalAmount ?? 0), 0).toFixed(2)} TJS
                                </span>
                            </div>
                        </div>

                        <div className="mt-4">
                            <button
                                className="w-full rounded-xl bg-red-100 py-3.5 font-semibold text-red-600 text-[15px] active:opacity-90 min-h-[48px]"
                                onClick={() => updateStatusTable(id!, tableData?.tableStatus ?? '', firstOrderId)}
                            >
                                Закрыть стол
                            </button>
                        </div>
                    </div>
                )}

                <IonAlert
                    ref={alert}
                    message={`Закрыть стол №${tableNumber}?`}
                    buttons={[
                        {
                            text: 'Нет',
                            role: 'cancel',
                            handler: () => alert.current?.dismiss(),
                        },
                        {
                            text: 'Да',
                            role: 'confirm',
                            handler: () => handleFreeTable(id!, firstOrderId),
                        },
                    ]}
                />
            </IonContent>
        </IonPage>
    );
};

export default OrderedTable;

const StatusTag: React.FC<{ status: OrderItemStatus }> = ({ status }) => {
    switch (status) {
        case 'preparing':
            return (
                <span className="flex items-center gap-1 text-orange-600">
                    <span className="h-2 w-2 rounded-full bg-orange-500" />
                    Готовится
                </span>
            );
        case 'ready':
            return (
                <span className="flex items-center gap-1 text-emerald-600">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    Готово
                </span>
            );
        case 'served':
            return (
                <span className="flex items-center gap-1 text-blue-600">
                    <span className="h-2 w-2 rounded-full bg-blue-500" />
                    Выдано
                </span>
            );
        case 'pending_confirmation':
            return  <span className="flex items-center gap-1 text-green-600"> 
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                             <span>Oжидает подтверждения</span>
                    </span> 
        case 'cancelled':
            return (
                <span className="flex items-center gap-1 text-sm text-red-700">
                    <span className="h-2 w-2 rounded-full bg-red-700" />
                    Отменён
                </span>
            );
        default:
            return null;
    }
};
